import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'

import { SupportAttendanceDay } from '@/app/services/supportRequestService/getSupportAttendanceStats'

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
)

const AVERAGE_COLOR = '#F59E0B'
const OPENED_COLOR = '#64748B'

// o tema muda por parceiro: a cor primária vem da variável CSS em runtime
const primaryColor = () => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary')
    .trim()
  return value ? `hsl(${value})` : '#00CC66'
}

const buildOptions = (days: SupportAttendanceDay[]): ChartOptions<'bar'> => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        boxHeight: 8,
        padding: 12,
        font: { size: 12, weight: 600 },
      },
    },
    tooltip: {
      callbacks: {
        title: (items) => {
          const day = days[items[0]?.dataIndex ?? 0]
          return day
            ? format(parseISO(day.date), 'EEEE, dd/MM', { locale: ptBR })
            : ''
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: {
      beginAtZero: true,
      border: { dash: [6, 4], display: false },
      ticks: { precision: 0, font: { size: 11 } },
    },
  },
})

interface AttendanceChartProps {
  days: SupportAttendanceDay[]
  barLabel: string
}

const periodDailyAverage = (days: SupportAttendanceDay[]) =>
  days.length
    ? days.reduce((sum, day) => sum + day.averageAttended, 0) / days.length
    : 0

const formatAverage = (value: number) =>
  value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

export const AttendanceChart = ({ days, barLabel }: AttendanceChartProps) => {
  const data = useMemo<ChartData<'bar' | 'line'>>(() => {
    const primary = primaryColor()
    const periodAverage = periodDailyAverage(days)
    return {
      labels: days.map((day) => format(parseISO(day.date), 'dd/MM')),
      datasets: [
        {
          type: 'line',
          label: `Média do período por atendente (${formatAverage(periodAverage)}/dia)`,
          data: days.map(() => periodAverage),
          borderColor: AVERAGE_COLOR,
          backgroundColor: AVERAGE_COLOR,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0,
          order: 1,
        },
        {
          type: 'line',
          label: 'Abertos no dia',
          data: days.map((day) => day.opened),
          borderColor: OPENED_COLOR,
          backgroundColor: OPENED_COLOR,
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 3,
          tension: 0.3,
          order: 2,
        },
        {
          type: 'bar',
          label: barLabel,
          data: days.map((day) => day.attended),
          backgroundColor: primary,
          borderRadius: 6,
          maxBarThickness: 28,
          order: 3,
        },
      ],
    }
  }, [days, barLabel])

  const options = useMemo(() => buildOptions(days), [days])

  return (
    <div className="h-[220px] w-full">
      <Chart
        type="bar"
        data={data as ChartData<'bar'>}
        options={options}
        aria-label="Gráfico de chamados atendidos e abertos por dia"
        role="img"
      />
    </div>
  )
}
